import { InputAdornment, TextField } from '@mui/material'
import React, { HTMLInputTypeAttribute } from 'react'

export interface CommonInputProps {
	type?: HTMLInputTypeAttribute
	disabled?: boolean
	readonly?: boolean
	fullWidth?: boolean
	variant?: 'filled' | 'outlined' | 'standard'
	multiline?: boolean
	multilineRows?: number
	disabledColor?: string
	helperText?: string
}

interface BaseInputProps extends CommonInputProps {
	label: string
	value: number | string | undefined | null
	onChange: (value: number | string | undefined) => void
	onBlur?: (value: number | string | undefined) => void
	endAdornment?: string
	minDate?: string
	maxDate?: string
	id?: string
	onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
}

/**
 * BaseInput component renders a base input field.
 *
 * @param {BaseInputProps} props - The properties for the PriceInput component
 * @returns {JSX.Element} The rendered BaseInput component
 */
const BaseInput = React.forwardRef<HTMLInputElement, BaseInputProps>(
	(
		{
			label,
			value,
			endAdornment,
			onChange,
			type,
			readonly,
			fullWidth = true,
			minDate,
			maxDate,
			variant,
			multiline = false,
			disabledColor,
			helperText,
			onBlur,
			id,
			multilineRows,
			...props
		},
		ref
	) => {
		return (
			<div className='input-container'>
				<TextField
					id={id}
					helperText={helperText}
					type={type}
					multiline={multiline}
					value={value}
					rows={multilineRows}
					label={label}
					onChange={e => onChange(e.target.value)}
					onBlur={e => (onBlur ? onBlur(e.target.value) : undefined)}
					inputProps={{ step: '0.01' }}
					variant={variant ?? 'outlined'}
					fullWidth={fullWidth}
					disabled={readonly}
					sx={
						disabledColor
							? {
									'& .MuiInputBase-input.Mui-disabled': {
										WebkitTextFillColor: disabledColor,
									},
									'&.MuiOutlinedInput-notchedOutline': { fontSize: '28px' },
								}
							: { '&.MuiOutlinedInput-notchedOutline': { fontSize: '28px' } }
					}
					InputProps={
						endAdornment || type === 'date'
							? {
									inputProps: { min: minDate, max: maxDate, format: 'DD-MM-YYYY' },
									endAdornment: endAdornment ? (
										<InputAdornment position='end'>{endAdornment}</InputAdornment>
									) : undefined,
								}
							: undefined
					}
					inputRef={ref}
					{...props}
				/>
			</div>
		)
	}
)

BaseInput.displayName = 'BaseInput'

export default BaseInput
